import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Search, Phone, MessageCircle, Eye, Edit3, FileText,
  UserPlus, Trash2, Check, X, Clock, AlertCircle, CheckCircle,
  ChevronDown, ChevronRight, Package, Calendar, RefreshCw
} from 'lucide-react';
import { useWorkflowStore } from '../store/workflowStore';
import { useCatalogStore } from '../store/catalogStore';
import { useAdminStore } from '../store/adminStore';
import { toast } from '../components/Toast';
import type { CustomerOrder, OrderStatus } from '../types/workflow';

const STATUS_COLORS: Record<OrderStatus, string> = {
  new:                   'bg-blue-100 text-blue-700',
  contacted:             'bg-yellow-100 text-yellow-700',
  quotation_created:     'bg-purple-100 text-purple-700',
  approved:              'bg-emerald-100 text-emerald-700',
  converted_to_invoice:  'bg-teal-100 text-teal-700',
  completed:             'bg-green-100 text-green-700',
  cancelled:             'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  new:                   'New',
  contacted:             'Contacted',
  quotation_created:     'Quotation Created',
  approved:              'Approved',
  converted_to_invoice:  'Converted to Invoice',
  completed:             'Completed',
  cancelled:             'Cancelled',
};

function fmt(d: string) {
  return new Date(d).toLocaleString('en-LK', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}
function fmtCurrency(n: number) {
  return `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function OrdersPage() {
  const { orders, updateOrderStatus, deleteOrder, createQuotationFromOrder, notifications, markNotificationRead } = useWorkflowStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [selected, setSelected] = useState<CustomerOrder | null>(null);
  const [editStatus, setEditStatus] = useState<{ orderId: string; current: OrderStatus } | null>(null);

  const filtered = useMemo(() => {
    return orders.filter(o => {
      const matchSearch =
        o.customerName.toLowerCase().includes(search.toLowerCase()) ||
        o.customerPhone.includes(search) ||
        o.referenceNumber.toLowerCase().includes(search.toLowerCase()) ||
        o.itemName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || o.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [orders, search, statusFilter]);

  // Stats
  const newOrders = orders.filter(o => o.status === 'new').length;
  const today = new Date().toDateString();
  const ordersToday = orders.filter(o => new Date(o.createdAt).toDateString() === today).length;
  const pendingFollowups = orders.filter(o => ['new', 'contacted'].includes(o.status)).length;

  const handleCreateQuotation = (orderId: string) => {
    const qid = createQuotationFromOrder(orderId);
    if (qid) {
      toast.success('Quotation Created', 'Redirecting to Quotations…');
      setTimeout(() => { window.location.hash = '#/admin/quotations'; window.location.href = '/admin/quotations'; }, 1200);
    } else {
      toast.error('Failed', 'Could not create quotation from this order.');
    }
  };

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleWhatsApp = (phone: string, name: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    const intl = cleaned.startsWith('0') ? '60' + cleaned.slice(1) : cleaned;
    window.open(`https://wa.me/${intl}?text=Hello ${encodeURIComponent(name)}, thank you for your interest.`, '_blank');
  };

  const handleStatusChange = (orderId: string, newStatus: OrderStatus) => {
    updateOrderStatus(orderId, newStatus);
    setEditStatus(null);
    toast.success('Status Updated', `Order status changed to ${STATUS_LABELS[newStatus]}`);
  };

  const handleDelete = (id: string) => {
    deleteOrder(id);
    if (selected?.id === id) setSelected(null);
    toast.info('Deleted', 'Order removed.');
  };

  const statuses: OrderStatus[] = ['new','contacted','quotation_created','approved','converted_to_invoice','completed','cancelled'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Website Orders</h1>
          <p className="text-sm text-gray-500 mt-0.5">Customer requests from the public website</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
            {newOrders} New
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'New Website Orders', value: newOrders, icon: ShoppingCart, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Orders Today', value: ordersToday, icon: Calendar, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Pending Follow-ups', value: pendingFollowups, icon: Clock, color: 'text-[#658C53]', bg: 'bg-[#F1F6EE]' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}>
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
            placeholder="Search orders, customers, reference…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >All</button>
          {statuses.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{STATUS_LABELS[s]}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <ShoppingCart className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">No orders found</p>
            <p className="text-xs mt-1">Orders from the public website will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product / Package</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qty</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(order => (
                  <motion.tr
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => setSelected(order)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-700">
                        {order.referenceNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{order.customerName}</div>
                      <div className="text-xs text-gray-500">{order.customerPhone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {order.itemImage && (
                          <img src={order.itemImage} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" />
                        )}
                        <div>
                          <div className="text-xs text-gray-900 max-w-[180px] truncate">{order.itemName}</div>
                          <div className="text-xs text-gray-400 capitalize">{order.itemType}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{order.quantity}</td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{fmtCurrency(order.itemPrice)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(order.createdAt)}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="relative">
                        <button
                          onClick={() => setEditStatus(editStatus?.orderId === order.id ? null : { orderId: order.id, current: order.status })}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[order.status]} flex items-center gap-1`}
                        >
                          {STATUS_LABELS[order.status]}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        <AnimatePresence>
                          {editStatus?.orderId === order.id && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              className="absolute top-8 left-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48"
                            >
                              {statuses.map(s => (
                                <button
                                  key={s}
                                  onClick={() => handleStatusChange(order.id, s)}
                                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 ${order.status === s ? 'text-[#7FA86A] font-medium' : 'text-gray-700'}`}
                                >
                                  {order.status === s && <Check className="w-3 h-3" />}
                                  {STATUS_LABELS[s]}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleCall(order.customerPhone)}
                          title="Call Customer"
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleWhatsApp(order.customerPhone, order.customerName)}
                          title="WhatsApp"
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setSelected(order)}
                          title="View Order"
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        {!['quotation_created','converted_to_invoice','completed','cancelled'].includes(order.status) && (
                          <button
                            onClick={() => handleCreateQuotation(order.id)}
                            title="Create Quotation"
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(order.id)}
                          title="Delete Order"
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Detail Drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setSelected(null)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 overflow-y-auto shadow-2xl"
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <h2 className="font-semibold text-gray-900">Order Details</h2>
                  <p className="text-xs text-gray-500 font-mono">{selected.referenceNumber}</p>
                </div>
                <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status]}`}>
                    {STATUS_LABELS[selected.status]}
                  </span>
                  <span className="text-xs text-gray-400">{fmt(selected.createdAt)}</span>
                </div>

                {/* Customer */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Customer</h3>
                  <div className="text-lg font-semibold text-gray-900">{selected.customerName}</div>
                  <div className="text-base text-gray-700 mt-1">{selected.customerPhone}</div>
                  {selected.customerEmail && <div className="text-sm text-gray-500 mt-1">{selected.customerEmail}</div>}
                  {selected.customerAddress && <div className="text-sm text-gray-500 mt-1">{selected.customerAddress}</div>}
                </div>

                {/* Product */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ordered Item</h3>
                  <div className="flex items-start gap-3">
                    {selected.itemImage && (
                      <img src={selected.itemImage} alt="" className="w-16 h-16 rounded-lg object-cover bg-gray-200" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{selected.itemName}</div>
                      <div className="text-xs text-gray-400 capitalize mt-0.5">{selected.itemType} • ID: {selected.itemId}</div>
                      <div className="text-sm font-semibold text-[#7FA86A] mt-2">{fmtCurrency(selected.itemPrice)} × {selected.quantity}</div>
                      <div className="text-sm font-bold text-gray-900">{fmtCurrency(selected.itemPrice * selected.quantity)}</div>
                    </div>
                  </div>
                </div>

                {/* Message */}
                {selected.message && (
                  <div className="bg-yellow-50 rounded-xl p-4">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message</h3>
                    <p className="text-sm text-gray-700">{selected.message}</p>
                  </div>
                )}

                {/* Order source */}
                <div className="text-xs text-gray-400">
                  Source: {selected.orderSource} | Order ID: {selected.id}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleCall(selected.customerPhone)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 rounded-xl text-sm font-medium hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-4 h-4" /> Call Customer
                  </button>
                  <button
                    onClick={() => handleWhatsApp(selected.customerPhone, selected.customerName)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 rounded-xl text-sm font-medium hover:bg-green-100 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                  {!['quotation_created','converted_to_invoice','completed','cancelled'].includes(selected.status) && (
                    <button
                      onClick={() => { handleCreateQuotation(selected.id); setSelected(null); }}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-100 transition-colors col-span-2"
                    >
                      <FileText className="w-4 h-4" /> Create Quotation from this Order
                    </button>
                  )}
                  <button
                    onClick={() => { handleStatusChange(selected.id, 'contacted'); setSelected({ ...selected, status: 'contacted' }); }}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-50 text-yellow-700 rounded-xl text-sm font-medium hover:bg-yellow-100 transition-colors col-span-2"
                  >
                    <Check className="w-4 h-4" /> Mark as Contacted
                  </button>
                  <button
                    onClick={() => { handleDelete(selected.id); setSelected(null); }}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors col-span-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete Order
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Close status dropdown on outside click */}
      {editStatus && (
        <div className="fixed inset-0 z-10" onClick={() => setEditStatus(null)} />
      )}
    </div>
  );
}
